import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import VouchModal from './VouchModal';

const ClaimedCodeCard = ({ claimedCode, onUpdate }) => {
  const [revealedCodes, setRevealedCodes] = useState({});
  const [copiedCode, setCopiedCode] = useState(null);
  const [showVouchModal, setShowVouchModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Safety check for data structure
  if (!claimedCode || !claimedCode.codes || claimedCode.codes.length === 0) {
    return null;
  }

  const handleRevealCode = (codeId) => {
    setRevealedCodes(prev => ({
      ...prev,
      [codeId]: !prev[codeId]
    }));
  };

  const handleCopyCode = async (code, codeId) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(codeId);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const formatPlatform = (platform) => {
    if (!platform) return 'Unknown';
    if (platform === 'other') return 'Other';
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="claimed-code-card">
      {/* Game Banner */}
      {claimedCode.banner && (
        <div className="claimed-code-banner">
          <img src={claimedCode.banner} alt={claimedCode.gameName} />
        </div>
      )}

      {/* Card Content */}
      <div className="claimed-code-content">
        <h3 className="claimed-game-title">{claimedCode.gameName}</h3>
        
        <div className="claimed-meta">
          <span className="claimed-platform">
            🎮 {formatPlatform(claimedCode.platform)}
          </span>
          {claimedCode.codes && claimedCode.codes[0] && claimedCode.codes[0].claimedAt && (
            <span className="claimed-date">
              📅 {formatDate(claimedCode.codes[0].claimedAt)}
            </span>
          )}
        </div>

        {/* Giver Info */}
        {claimedCode.giver && (
          <div className="claimed-giver">
            <span>From: </span>
            <Link to={`/profile/${claimedCode.giver.username}`} className="giver-link">
              {(claimedCode.giver.avatar?.url || (typeof claimedCode.giver.avatar === 'string' && claimedCode.giver.avatar)) && !imageError ? (
                <img 
                  src={claimedCode.giver.avatar?.url || claimedCode.giver.avatar} 
                  alt={claimedCode.giver.username} 
                  className="giver-avatar"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="avatar-placeholder" style={{ width: '28px', height: '28px', fontSize: '14px', border: '2px solid rgba(255, 255, 255, 0.1)' }}>
                  {claimedCode.giver.username.charAt(0).toUpperCase()}
                </div>
              )}
              <span className={`giver-username ${claimedCode.giver.accountType || ''}`}>
                {claimedCode.giver.username}
              </span>
            </Link>
          </div>
        )}

        {/* Code Display */}
        {claimedCode.codes && claimedCode.codes.length > 0 && claimedCode.codes.map((codeObj) => (
          <div key={codeObj._id} className="code-display-section">
            <div className="code-box">
              <div className={`code-value ${!revealedCodes[codeObj._id] ? 'blurred' : ''}`}>
                {revealedCodes[codeObj._id] ? codeObj.code : '••••-••••-••••-••••'}
              </div>
              <div className="code-actions">
                <button
                  onClick={() => handleRevealCode(codeObj._id)}
                  className="reveal-btn"
                  title={revealedCodes[codeObj._id] ? 'Hide Code' : 'Reveal Code'}
                >
                  {revealedCodes[codeObj._id] ? '👁️' : '👁️‍🗨️'}
                </button>
                <button
                  onClick={() => handleCopyCode(codeObj.code, codeObj._id)}
                  className={`copy-btn ${copiedCode === codeObj._id ? 'copied' : ''}`}
                  title="Copy Code"
                  disabled={!revealedCodes[codeObj._id]}
                >
                  {copiedCode === codeObj._id ? '✓ Copied!' : '📋 Copy'}
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Redemption Help */}
        <div className="redemption-help">
          <details>
            <summary>📝 How to Redeem</summary>
            <ol>
              <li>Reveal and copy the code above</li>
              <li>Open your {formatPlatform(claimedCode.platform)} platform/store</li>
              <li>Find "Redeem Code" or "Activate Product"</li>
              <li>Paste the code and activate</li>
              <li>Download and enjoy your game!</li>
            </ol>
          </details>
        </div>

        {/* Actions */}
        <div className="claimed-actions">
          {claimedCode.gameUrl && (
            <a 
              href={claimedCode.gameUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="game-link-btn"
            >
              🔗 View Game
            </a>
          )}
          {claimedCode.giver && (
            <button 
              onClick={() => setShowVouchModal(true)}
              className="vouch-btn"
            >
              ⭐ Vouch for {claimedCode.giver.username}
            </button>
          )}
        </div>
      </div>

      {/* Vouch Modal */}
      {showVouchModal && claimedCode.giver && (
        <VouchModal
          toUserId={claimedCode.giver._id}
          toUsername={claimedCode.giver.username}
          codeRequestId={claimedCode.codeRequestId}
          gameName={claimedCode.gameName}
          onClose={() => setShowVouchModal(false)}
          onSuccess={() => {
            setShowVouchModal(false);
            if (onUpdate) onUpdate();
          }}
        />
      )}
    </div>
  );
};

export default ClaimedCodeCard;
